// ═══════════════════════════════════════════════════════════════
// TICKET 360 — Complete audit trail for a single load ticket
// Shows chain of custody, billing, truck cert, disposal site,
// related documents, and generates single-ticket FEMA audit PDF.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Button, Stack, SimpleGrid, Divider, Modal,
} from '../../components/gab/GabComponents';
import {
  IconArrowLeft, IconFileText, IconTruck, IconMapPin,
  IconBuilding, IconReceipt, IconShield, IconAlertTriangle,
  IconClock, IconUser, IconDownload, IconFileTypePdf, IconSearch,
  IconPaperclip, IconEye,
} from '@tabler/icons-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

import { fetchRecords, queryRecords, fetchRecord, updateRecord } from '../../api/client';
import { useClinDescriptions } from '../../hooks/useClinDescriptions';
import { getV2FileDownloadUrl, uploadFileToV2 } from '../../lib/uploadFile';
import { notifications } from '../../lib/notifications';
import PdfTicketViewer from '../../components/ui/PdfTicketViewer';
import TicketHtmlView from '../../components/ui/TicketHtmlView';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';
import { useDebrisStore } from '../../store/debris-store';
import { DEBRIS_TYPE_LABELS, DEBRIS_TYPE_COLORS, TICKET_STATUS_LABELS, TICKET_STATUS_COLORS, decodeTicketDocs, encodeTicketDocs } from '../../types/debris';
import type { DebrisType, TicketStatus } from '../../types/debris';

// ── Formatters ──────────────────────────────────────────────
const fmtDate = (d: string) => {
  if (!d) return '-';
  const s = d.slice(0, 10);
  if (s.includes('-')) { const [y, m, day] = s.split('-'); return `${m}/${day}/${y}`; }
  return s;
};
const fmtMoney = (v: number) => v > 0 ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-';
const fmtTime = (d: string) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleString(); } catch { return d; }
};

// ── TRMETA decode (truck cert metadata) ────────────────────
const TRMETA_RE = /\|\|\|TRMETA\|\|\|([\s\S]*?)\|\|\|ENDTRMETA\|\|\|/;
function decodeTrMeta(notes: string): Record<string, string> {
  if (!notes) return {};
  const m = notes.match(TRMETA_RE);
  if (!m) return {};
  try { return JSON.parse(m[1]); } catch { return {}; }
}

// ── DOCID decode (evidence document metadata) ──────────────
function parseDocId(notes: string): string {
  if (!notes) return '';
  const m = notes.match(/\|\|\|DOCID\|\|\|([^|]+)\|\|\|/);
  return m ? m[1] : '';
}

// ── TMETA decode ────────────────────────────────────────────
const TMETA_RE = /\|\|\|TMETA\|\|\|([\s\S]*?)\|\|\|ENDTMETA\|\|\|/;
function decodeMeta(notes: string): Record<string, string> {
  if (!notes) return {};
  const m = notes.match(TMETA_RE);
  if (!m) return {};
  try { return JSON.parse(m[1]); } catch { return {}; }
}
function cleanNotes(notes: string): string {
  return (notes ?? '').replace(TMETA_RE, '').trim();
}

// ── Field shortcuts ─────────────────────────────────────────
const FL = FIELDS.debrisLoadTickets;
const FT = FIELDS.debrisTrucks;
const FS = FIELDS.debrisDisposalSites;
const FC = FIELDS.debrisContractors;
const FR = FIELDS.debrisRateSchedules;
const FI = FIELDS.debrisInvoices;
const FD = FIELDS.debrisDocuments;
const FRC = FIELDS.debrisReconciliations;

// ── Status colors ───────────────────────────────────────────
const STATUS_COLORS: Record<string, string> = {
  draft: '#939598', in_transit: 'var(--color-primary-700)', at_site: '#C68700',
  verified: 'var(--color-success)', delivered: '#03AC9E',
  disputed: 'var(--color-error)', voided: '#939598',
};

// ── Types for related data ──────────────────────────────────
interface TruckInfo {
  id: number; truckNumber: string; licensePlate: string; truckType: string;
  capacityCY: string; hasTailgate: string; certStatus: string;
  certifiedDate: string; certExpiresAt: string; placardNumber: string;
  driverName: string; notes: string;
}
interface SiteInfo {
  id: number; siteName: string; siteType: string; address: string;
  capacityCubicYards: string; currentVolume: string; acceptedTypes: string;
  notes: string; status: string;
}
interface ContractorInfo {
  id: number; companyName: string; contractNumber: string; contactName: string;
  contactPhone: string; contactEmail: string; status: string; notes: string;
}
interface RateInfo {
  id: number; lineItemCode: string; description: string; unit: string;
  rateTier1: string; category: string;
}
interface InvoiceInfo {
  id: number; invoiceNumber: string; periodStart: string; periodEnd: string;
  totalAmount: string; status: string;
}
interface DocInfo {
  id: number; title: string; documentType: string; uploadedBy: string;
  notes: string; createdAt: string;
}
interface ReconciliationInfo {
  id: number; status: string; totalInvoiced: string; totalVerified: string;
  totalVariance: string; periodStart: string; periodEnd: string;
}

// ── Ticket data (raw row + decoded meta) ────────────────────
interface TicketData {
  id: number;
  ticketNumber: string; contractorId: string; debrisType: string;
  calculatedCubicYards: string; status: string;
  loadingMonitorName: string; towerMonitorName: string;
  loadingLoadPct: string; towerLoadPct: string;
  disposalSiteId: string; notes: string; zoneId: string;
  loadingMonitorSignature: string; driverPickupSignature: string;
  towerMonitorSignature: string; driverDeliverySignature: string;
  verifiedAt: string; handLoaded: string; truckId: string;
  pickupStopsJson: string; measurementMode: string;
  unitCount: string; weight: string;
  // From meta
  meta: Record<string, string>;
  cleanNotes: string;
}

export default function Ticket360() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();

  const { clinLabel, clinDesc } = useClinDescriptions();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfLoading, setPdfLoading] = useState<string | null>(null);
  const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);
  const [pdfViewerTitle, setPdfViewerTitle] = useState('');
  const [ticket, setTicket] = useState<TicketData | null>(null);
  const [ticketIncidentId, setTicketIncidentId] = useState('');
  const [truck, setTruck] = useState<TruckInfo | null>(null);
  const [site, setSite] = useState<SiteInfo | null>(null);
  const [contractor, setContractor] = useState<ContractorInfo | null>(null);
  const [rateSchedule, setRateSchedule] = useState<RateInfo | null>(null);
  const [invoices, setInvoices] = useState<InvoiceInfo[]>([]);
  const [reconciliations, setReconciliations] = useState<ReconciliationInfo[]>([]);
  const [documents, setDocuments] = useState<DocInfo[]>([]);
  const [ticketDocs, setTicketDocs] = useState<{ haulTicketDocId?: string; weighTicketDocId?: string; monitoringLogDocId?: string }>({});
  const [docSaving, setDocSaving] = useState<string | null>(null);

  // ── View PDF helper — opens inline viewer modal via proxy ──
  const [pdfSearchText, setPdfSearchText] = useState('');
  const [pdfViewerPage, setPdfViewerPage] = useState<number | undefined>(undefined);
  const [showTicketHtml, setShowTicketHtml] = useState(false);

  const viewPdf = async (documentId: string, title?: string, searchText?: string, pageNum?: number) => {
    if (!documentId) return;
    setPdfLoading(documentId);
    try {
      const rawUrl = await getV2FileDownloadUrl(documentId);
      // Proxy for inline display (strips Content-Disposition: attachment)
      // Use browser native viewer via iframe, not pdf.js
      const proxyUrl = `/api/filepreview?url=${encodeURIComponent(rawUrl)}`;
      const pageHash = pageNum ? `#page=${pageNum}` : '';
      setPdfViewerUrl(proxyUrl + pageHash);
      setPdfViewerTitle(title || 'Document Viewer');
      setPdfSearchText(searchText || '');
      setPdfViewerPage(pageNum);
    } catch (e) {
      console.error('Failed to get download URL:', e);
      notifications.show({ message: 'Could not load document', color: 'var(--color-error)' });
    } finally {
      setPdfLoading(null);
    }
  };

  const saveTicketDoc = async (slot: 'haulTicketDocId' | 'weighTicketDocId' | 'monitoringLogDocId', file: File) => {
    if (!ticket || !ticketId) return;
    setDocSaving(slot);
    try {
      const { documentId: docId } = await uploadFileToV2(file, `ticket-${ticket.ticketNumber}-${slot}`);
      const updated = { ...ticketDocs, [slot]: docId };
      setTicketDocs(updated);
      // Store in ticket notes
      const rawNotes = ticket.notes || '';
      const cleanedNotes = rawNotes.replace(/\|\|\|TICKETDOCS\|\|\|[\s\S]*?\|\|\|ENDTICKETDOCS\|\|\|/, '').trim();
      const newNotes = cleanedNotes + '\n' + encodeTicketDocs(updated);
      await updateRecord(TABLES.debrisLoadTickets, Number(ticketId), { [FIELDS.debrisLoadTickets.notes]: newNotes });
      notifications.show({ message: `${slot.replace('DocId', '')} attached`, color: 'var(--color-success)' });
    } catch (e) {
      notifications.show({ message: 'Upload failed', color: 'var(--color-error)' });
    } finally {
      setDocSaving(null);
    }
  };

  // ── Load all data ─────────────────────────────────────────
  useEffect(() => {
    if (!ticketId) return;
    setLoading(true);
    setError('');

    const rid = Number(ticketId);

    (async () => {
      try {
        // 1. Fetch the ticket record
        const ticketRow = await fetchRecord<Record<string, unknown>>(
          TABLES.debrisLoadTickets, rid
        );
        if (!ticketRow) { setError('Ticket not found'); setLoading(false); return; }

        const rawNotes = String(ticketRow[FL.notes] ?? '');
        const ticketIncidentId = String(ticketRow[FL.incidentId] ?? '');
        setTicketIncidentId(ticketIncidentId);
        const rawMeta = decodeMeta(rawNotes);
        const clean = cleanNotes(rawNotes);
        // Merge: start from TMETA (for non-migrated fields like loadPercentage, disposalTime, etc.)
        // then overlay real column values for all migrated fields
        const meta = {
          ...rawMeta,
          truckNumber:       String(ticketRow[FL.truckNumber] ?? ''),
          clinCode:          String(ticketRow[FL.clinCode] ?? ''),
          clinDescription:   String(ticketRow[FL.clinDescription] ?? ''),
          distanceMiles:     String(ticketRow[FL.distanceMiles] ?? ''),
          transactionRate:   String(ticketRow[FL.transactionRate] ?? ''),
          extendedCost:      String(ticketRow[FL.extendedCost] ?? ''),
          subcontractorName: String(ticketRow[FL.subcontractorName] ?? ''),
          pickupLat:         String(ticketRow[FL.pickupLat] ?? ''),
          pickupLng:         String(ticketRow[FL.pickupLng] ?? ''),
          pickupAddress:     String(ticketRow[FL.pickupAddress] ?? ''),
          pickupTime:        String(ticketRow[FL.pickupTime] ?? ''),
          disposalSiteName:  String(ticketRow[FL.disposalSiteName] ?? ''),
          loadDate:          String(ticketRow[FL.loadDate] ?? ''),
          roadName:          String(ticketRow[FL.roadName] ?? ''),
          roadOwner:         String(ticketRow[FL.roadOwner] ?? ''),
          roadOwnerName:     String(ticketRow[FL.roadOwnerName] ?? ''),
          roadClass:         String(ticketRow[FL.roadClass] ?? ''),
        };

        const td: TicketData = {
          id: rid,
          ticketNumber: String(ticketRow[FL.ticketNumber] ?? ''),
          contractorId: String(ticketRow[FL.contractorId] ?? ''),
          debrisType: String(ticketRow[FL.debrisType] ?? ''),
          calculatedCubicYards: String(ticketRow[FL.calculatedCubicYards] ?? ''),
          status: String(ticketRow[FL.status] ?? 'draft'),
          loadingMonitorName: String(ticketRow[FL.loadingMonitorName] ?? ''),
          towerMonitorName: String(ticketRow[FL.towerMonitorName] ?? ''),
          loadingLoadPct: String(ticketRow[FL.loadingLoadPct] ?? ''),
          towerLoadPct: String(ticketRow[FL.towerLoadPct] ?? ''),
          disposalSiteId: String(ticketRow[FL.disposalSiteId] ?? ''),
          notes: rawNotes,
          zoneId: String(ticketRow[FL.zoneId] ?? ''),
          loadingMonitorSignature: String(ticketRow[FL.loadingMonitorSignature] ?? ''),
          driverPickupSignature: String(ticketRow[FL.driverPickupSignature] ?? ''),
          towerMonitorSignature: String(ticketRow[FL.towerMonitorSignature] ?? ''),
          driverDeliverySignature: String(ticketRow[FL.driverDeliverySignature] ?? ''),
          verifiedAt: String(ticketRow[FL.verifiedAt] ?? ''),
          handLoaded: String(ticketRow[FL.handLoaded] ?? ''),
          truckId: String(ticketRow[FL.truckId] ?? ''),
          pickupStopsJson: String(ticketRow[FL.pickupStopsJson] ?? ''),
          measurementMode: String(ticketRow[FL.measurementMode] ?? '') || meta.measurementMode || 'volumetric',
          unitCount: String(ticketRow[FL.unitCount] ?? ''),
          weight: String(ticketRow[FL.weight] ?? ''),
          meta,
          cleanNotes: clean,
        };
        setTicket(td);
        const parsedTicketDocs = decodeTicketDocs(rawNotes);

        // Auto-search debrisDocuments for a load ticket PDF matching this ticket number
        // This links PDFs uploaded via Data Intake that weren't manually attached
        if (!parsedTicketDocs.haulTicketDocId && td.ticketNumber) {
          try {
            const FDoc = FIELDS.debrisDocuments;
            const docSearch = await queryRecords<Record<string, unknown>>(TABLES.debrisDocuments, {
              limit: 50,
              ...(ticketIncidentId ? { filters: [{ field: FDoc.incidentId, operator: 'eq' as const, value: ticketIncidentId }] } : {}),
            });
            const ticketNum = td.ticketNumber.toLowerCase();
            const matchedDoc = docSearch.records.find(r => {
              const docType = String(r[FDoc.documentType] || '');
              const title = String(r[FDoc.title] || '').toLowerCase();
              const notes = String(r[FDoc.notes] || '').toLowerCase();
              return (docType === 'load_tickets' || docType === 'ticket_image') &&
                (title.includes(ticketNum) || notes.includes(ticketNum));
            });
            if (matchedDoc) {
              const docNotes = String(matchedDoc[FDoc.notes] || '');
              // DOCID marker has the V2 document UUID (needed for download-url)
              // FDoc.file has the attachmentId (NOT usable for download-url)
              const docIdMatch = docNotes.match(/\|\|\|DOCID\|\|\|([^|]+)\|\|\|/);
              const fileField = matchedDoc[FDoc.file];
              const fileDocId = fileField ? String(typeof fileField === 'object' ? (fileField as any).id || '' : fileField) : '';
              const resolvedId = docIdMatch?.[1] || fileDocId || '';
              if (resolvedId) {
                parsedTicketDocs.haulTicketDocId = resolvedId;
              }
              // Extract page number from PAGEMAP marker
              const pageMapMatch = docNotes.match(/\|\|\|PAGEMAP\|\|\|([\s\S]*?)\|\|\|ENDPAGEMAP\|\|\|/);
              if (pageMapMatch) {
                try {
                  const pageMap = JSON.parse(pageMapMatch[1]);
                  const pageNum = pageMap[td.ticketNumber];
                  if (pageNum) (parsedTicketDocs as any).haulTicketPage = pageNum;
                } catch {}
              }
            }
          } catch { /* non-critical — manual attach still available */ }
        }
        setTicketDocs(parsedTicketDocs);

        // 2. Fetch related data in parallel
        const [trucksRes, sitesRes, contractorsRes, ratesRes, invoicesRes, docsRes, reconcRes] =
          await Promise.all([
            fetchRecords<Record<string, unknown>>(TABLES.debrisTrucks, { limit: 500 }),
            fetchRecords<Record<string, unknown>>(TABLES.debrisDisposalSites, { limit: 200 }),
            fetchRecords<Record<string, unknown>>(TABLES.debrisContractors, { limit: 200 }),
            fetchRecords<Record<string, unknown>>(TABLES.debrisRateSchedules, { limit: 200 }),
            fetchRecords<Record<string, unknown>>(TABLES.debrisInvoices, { limit: 200 }),
            queryRecords<Record<string, unknown>>(TABLES.debrisDocuments, { limit: 500 }),
            fetchRecords<Record<string, unknown>>(TABLES.debrisReconciliations, { limit: 200 }),
          ]);

        // 3. Match truck by truckNumber from meta or truckId
        const truckNumber = meta.truckNumber || '';
        const truckIdNum = td.truckId ? Number(td.truckId) : 0;
        const matchedTruck = trucksRes.records.find((r: Record<string, unknown>) =>
          (truckNumber && String(r[FT.truckNumber] ?? '') === truckNumber) ||
          (truckIdNum && (Number(r.record_id) === truckIdNum))
        );
        if (matchedTruck) {
          setTruck({
            id: Number(matchedTruck.record_id),
            truckNumber: String(matchedTruck[FT.truckNumber] ?? ''),
            licensePlate: String(matchedTruck[FT.licensePlate] ?? ''),
            truckType: String(matchedTruck[FT.truckType] ?? ''),
            capacityCY: String(matchedTruck[FT.capacityCY] ?? ''),
            hasTailgate: String(matchedTruck[FT.hasTailgate] ?? ''),
            certStatus: String(matchedTruck[FT.certStatus] ?? ''),
            certifiedDate: String(matchedTruck[FT.certifiedDate] ?? ''),
            certExpiresAt: String(matchedTruck[FT.certExpiresAt] ?? ''),
            placardNumber: String(matchedTruck[FT.placardNumber] ?? ''),
            driverName: String(matchedTruck[FT.driverName] ?? ''),
            notes: String(matchedTruck[FT.notes] ?? ''),
          });
        }

        // 4. Match disposal site
        const siteId = td.disposalSiteId ? Number(td.disposalSiteId) : 0;
        const disposalSiteName = meta.disposalSiteName || '';
        const matchedSite = sitesRes.records.find((r: Record<string, unknown>) =>
          (siteId && Number(r.record_id) === siteId) ||
          (disposalSiteName && String(r[FS.siteName] ?? '').toLowerCase() === disposalSiteName.toLowerCase())
        );
        if (matchedSite) {
          setSite({
            id: Number(matchedSite.record_id),
            siteName: String(matchedSite[FS.siteName] ?? ''),
            siteType: String(matchedSite[FS.siteType] ?? ''),
            address: String(matchedSite[FS.address] ?? ''),
            capacityCubicYards: String(matchedSite[FS.capacityCubicYards] ?? ''),
            currentVolume: String(matchedSite[FS.currentVolume] ?? ''),
            acceptedTypes: String(matchedSite[FS.acceptedTypes] ?? ''),
            notes: String(matchedSite[FS.notes] ?? ''),
            status: String(matchedSite[FS.status] ?? ''),
          });
        }

        // 5. Match contractor
        const contractorIdNum = td.contractorId ? Number(td.contractorId) : 0;
        const contractorName = meta.contractorName || '';
        const matchedContractor = contractorsRes.records.find((r: Record<string, unknown>) =>
          (contractorIdNum && Number(r.record_id) === contractorIdNum) ||
          (contractorName && String(r[FC.companyName] ?? '').toLowerCase().includes(contractorName.toLowerCase()))
        );
        if (matchedContractor) {
          setContractor({
            id: Number(matchedContractor.record_id),
            companyName: String(matchedContractor[FC.companyName] ?? ''),
            contractNumber: String(matchedContractor[FC.contractNumber] ?? ''),
            contactName: String(matchedContractor[FC.contactName] ?? ''),
            contactPhone: String(matchedContractor[FC.contactPhone] ?? ''),
            contactEmail: String(matchedContractor[FC.contactEmail] ?? ''),
            status: String(matchedContractor[FC.status] ?? ''),
            notes: String(matchedContractor[FC.notes] ?? ''),
          });
        }

        // 6. Match rate schedule by clinCode
        const clinCode = meta.clinCode || '';
        if (clinCode) {
          const matchedRate = ratesRes.records.find((r: Record<string, unknown>) =>
            String(r[FR.lineItemCode] ?? '').toUpperCase() === clinCode.toUpperCase()
          );
          if (matchedRate) {
            setRateSchedule({
              id: Number(matchedRate.record_id),
              lineItemCode: String(matchedRate[FR.lineItemCode] ?? ''),
              description: String(matchedRate[FR.description] ?? ''),
              unit: String(matchedRate[FR.unit] ?? ''),
              rateTier1: String(matchedRate[FR.rateTier1] ?? ''),
              category: String(matchedRate[FR.category] ?? ''),
            });
          }
        }

        // 7. Match invoices by contractor + period overlap
        const inv: InvoiceInfo[] = invoicesRes.records
          .filter((r: Record<string, unknown>) => {
            if (contractorIdNum && String(r[FI.contractorId] ?? '') === td.contractorId) return true;
            return false;
          })
          .map((r: Record<string, unknown>) => ({
            id: Number(r.record_id),
            invoiceNumber: String(r[FI.invoiceNumber] ?? ''),
            periodStart: String(r[FI.periodStart] ?? ''),
            periodEnd: String(r[FI.periodEnd] ?? ''),
            totalAmount: String(r[FI.totalAmount] ?? ''),
            status: String(r[FI.status] ?? ''),
          }));
        setInvoices(inv);

        // 8. Match reconciliations
        const recs: ReconciliationInfo[] = reconcRes.records
          .filter((r: Record<string, unknown>) => {
            if (contractorIdNum && String(r[FRC.contractorId] ?? '') === td.contractorId) return true;
            return false;
          })
          .map((r: Record<string, unknown>) => ({
            id: Number(r.record_id),
            status: String(r[FRC.status] ?? ''),
            totalInvoiced: String(r[FRC.totalInvoiced] ?? ''),
            totalVerified: String(r[FRC.totalVerified] ?? ''),
            totalVariance: String(r[FRC.totalVariance] ?? ''),
            periodStart: String(r[FRC.periodStart] ?? ''),
            periodEnd: String(r[FRC.periodEnd] ?? ''),
          }));
        setReconciliations(recs);

        // 9. Match documents by ticket number, truck number, OR ticket_image type
        const ticketNum = td.ticketNumber;
        // Use the resolved truck number from the matched truck record (not meta.truckNumber which is often empty)
        const resolvedTruckNumber = (matchedTruck ? String(matchedTruck[FT.truckNumber] ?? '') : '') || truckNumber;
        const docs: DocInfo[] = docsRes.records
          .filter((r: Record<string, unknown>) => {
            const title = String(r[FD.title] ?? '').toLowerCase();
            const notes = String(r[FD.notes] ?? '').toLowerCase();
            const docType = String(r[FD.documentType] ?? '');
            // Always include ticket_image and ticket_page_index docs
            if (docType === 'ticket_image' || docType === 'ticket_page_index') return true;
            // Match by ticket number
            if (ticketNum && (title.includes(ticketNum.toLowerCase()) || notes.includes(ticketNum.toLowerCase()))) return true;
            // Match truck cert by type + truck number in title or notes
            const docIncidentId = String(r[FD.incidentId] ?? '');
            const certIncidentOk = !ticketIncidentId || !docIncidentId || docIncidentId === ticketIncidentId;
            const isCertType = docType === 'truck_certification' || docType === 'truck_cert';
            if (resolvedTruckNumber && isCertType && certIncidentOk &&
                (title.includes(resolvedTruckNumber.toLowerCase()) || notes.includes(resolvedTruckNumber.toLowerCase()))) return true;
            // Legacy: TRUCKNUM tag in notes
            if (resolvedTruckNumber && notes.includes('|||trucknum|||' + resolvedTruckNumber.toLowerCase() + '|||') && certIncidentOk) return true;
            return false;
          })
          .map((r: Record<string, unknown>) => ({
            id: Number(r.record_id),
            title: String(r[FD.title] ?? ''),
            documentType: String(r[FD.documentType] ?? ''),
            uploadedBy: String(r[FD.uploadedBy] ?? ''),
            notes: String(r[FD.notes] ?? ''),
            createdAt: String((r as any).created_at ?? ''),
          }));
        setDocuments(docs);

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load ticket data');
      } finally {
        setLoading(false);
      }
    })();
  }, [ticketId]);

  // ── Derived values ────────────────────────────────────────
  const meta = ticket?.meta ?? {};
  const truckNumber = meta.truckNumber || '';
  const clinCode = meta.clinCode || '';
  const clinDescription = meta.clinDescription || rateSchedule?.description || '';
  const transactionRate = parseFloat(meta.transactionRate || '0');
  const extendedCost = parseFloat(meta.extendedCost || '0');
  const cy = parseFloat(ticket?.calculatedCubicYards || '0');
  const truckCapacity = parseFloat(truck?.capacityCY || '0');
  const capacityExceeded = truckCapacity > 0 && cy > truckCapacity;

  // Disposal site waste type compliance check
  const siteAcceptedTypes = useMemo(() => {
    if (!site?.acceptedTypes) return [];
    try { return JSON.parse(site.acceptedTypes); } catch { return site.acceptedTypes.split(',').map((s: string) => s.trim()); }
  }, [site]);

  const debrisTypeLabel = DEBRIS_TYPE_LABELS[ticket?.debrisType as DebrisType] ?? ticket?.debrisType ?? '';
  const wasteTypeViolation = site && siteAcceptedTypes.length > 0 && ticket?.debrisType &&
    !siteAcceptedTypes.some((t: string) => t.toLowerCase().includes(ticket.debrisType.toLowerCase()) ||
      ticket.debrisType.toLowerCase().includes(t.toLowerCase()));

  // ── PDF Generation ────────────────────────────────────────
  const [exporting, setExporting] = useState(false);

  const generatePDF = async () => {
    if (!ticket) return;
    setExporting(true);

    try {
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();
    let y = 15;

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(`Ticket 360 Audit Report`, 14, y);
    y += 8;
    doc.setFontSize(12);
    doc.text(`Ticket #${ticket.ticketNumber}`, 14, y);
    y += 6;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, y);
    y += 10;

    // Page 1: Ticket Details
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Ticket Summary', 14, y);
    y += 6;

    autoTable(doc, {
      startY: y,
      head: [['Field', 'Value']],
      body: [
        ['Ticket Number', ticket.ticketNumber],
        ['Status', TICKET_STATUS_LABELS[ticket.status as TicketStatus] ?? ticket.status],
        ['Debris Type', debrisTypeLabel],
        ['Cubic Yards', ticket.calculatedCubicYards || '-'],
        ['CLIN Code', clinLabel(clinCode)],
        ['Rate', transactionRate > 0 ? fmtMoney(transactionRate) : '-'],
        ['Extended Cost', extendedCost > 0 ? fmtMoney(extendedCost) : '-'],
        ['Contractor', contractor?.companyName || meta.contractorName || '-'],
        ['Subcontractor', meta.subcontractorName || '-'],
      ],
      theme: 'grid',
      styles: { fontSize: 9 },
      headStyles: { fillColor: [75, 63, 255] },
    });
    y = (doc as any).lastAutoTable.finalY + 10;

    // Chain of Custody
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Chain of Custody', 14, y);
    y += 6;

    autoTable(doc, {
      startY: y,
      head: [['Stage', 'Detail']],
      body: [
        ['Pickup Date/Time', `${meta.loadDate || '-'} ${meta.loadTime || ''}`],
        ['Pickup Location', `${meta.pickupLat || '-'}, ${meta.pickupLng || ''}`],
        ['Road', meta.roadName || '-'],
        ['Road Owner', meta.roadOwnerName || '-'],
        ['Road Class', meta.roadClass || '-'],
        ['Loading Monitor', `${meta.loadMonitorName || ticket.loadingMonitorName || '-'} (${meta.loadMonitorId || '-'})`],
        ['Loading Load %', `${ticket.loadingLoadPct || '-'}%`],
        ['Truck Number', truckNumber || '-'],
        ['Truck Capacity', truck ? `${truck.capacityCY} CY` : '-'],
        ['Disposal Date/Time', `${meta.disposalDate || '-'} ${meta.disposalTime || ''}`],
        ['Disposal Site', meta.disposalSiteName || site?.siteName || '-'],
        ['Disposal Monitor', `${meta.disposalMonitorName || ticket.towerMonitorName || '-'} (${meta.disposalMonitorId || '-'})`],
        ['Tower Load %', `${ticket.towerLoadPct || '-'}%`],
        ['Verified CY', ticket.calculatedCubicYards || '-'],
      ],
      theme: 'grid',
      styles: { fontSize: 9 },
      headStyles: { fillColor: [75, 63, 255] },
    });
    y = (doc as any).lastAutoTable.finalY + 10;

    // Page 2: Truck Certification
    if (y > 240) { doc.addPage(); y = 15; }
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Truck Certification', 14, y);
    y += 6;

    if (truck) {
      autoTable(doc, {
        startY: y,
        head: [['Field', 'Value']],
        body: [
          ['Truck Number', truck.truckNumber],
          ['License Plate', truck.licensePlate || '-'],
          ['Type', truck.truckType || '-'],
          ['Capacity (CY)', truck.capacityCY || '-'],
          ['Cert Status', truck.certStatus || '-'],
          ['Certified Date', fmtDate(truck.certifiedDate)],
          ['Cert Expires', fmtDate(truck.certExpiresAt)],
          ['Placard Number', truck.placardNumber || '-'],
          ['Has Tailgate', truck.hasTailgate === 'true' ? 'Yes' : 'No'],
          ['Driver', truck.driverName || '-'],
        ],
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [75, 63, 255] },
      });
      y = (doc as any).lastAutoTable.finalY + 10;
      if (capacityExceeded) {
        doc.setTextColor(211, 52, 35);
        doc.setFont('helvetica', 'bold');
        doc.text(`WARNING: Load exceeds certified capacity (${cy} CY hauled vs ${truckCapacity} CY certified)`, 14, y);
        doc.setTextColor(0, 0, 0);
        y += 8;
      }
    } else {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`No truck certification on file for truck #${truckNumber || ticket.truckId || 'unknown'}`, 14, y);
      y += 8;
    }

    // Page 3: Disposal Site
    if (y > 240) { doc.addPage(); y = 15; }
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Disposal Site Authorization', 14, y);
    y += 6;

    if (site) {
      autoTable(doc, {
        startY: y,
        head: [['Field', 'Value']],
        body: [
          ['Site Name', site.siteName],
          ['Type', site.siteType || '-'],
          ['Address', site.address || '-'],
          ['Capacity (CY)', site.capacityCubicYards || '-'],
          ['Current Volume', site.currentVolume || '-'],
          ['Accepted Types', siteAcceptedTypes.join(', ') || '-'],
          ['Status', site.status || '-'],
        ],
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [75, 63, 255] },
      });
      y = (doc as any).lastAutoTable.finalY + 10;
    } else {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('No disposal site record found.', 14, y);
      y += 8;
    }

    // Page 4: Billing
    if (y > 240) { doc.addPage(); y = 15; }
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Billing Detail', 14, y);
    y += 6;

    autoTable(doc, {
      startY: y,
      head: [['Field', 'Value']],
      body: [
        ['CLIN Code', clinLabel(clinCode)],
        ['Description', clinDescription || clinDesc(clinCode) || '-'],
        ['Rate ($/CY)', transactionRate > 0 ? fmtMoney(transactionRate) : '-'],
        ['Quantity (CY)', ticket.calculatedCubicYards || '-'],
        ['Extended Cost', extendedCost > 0 ? fmtMoney(extendedCost) : '-'],
        ['Invoice(s)', invoices.length > 0 ? invoices.map(i => `#${i.invoiceNumber} (${i.status})`).join(', ') : 'None found'],
        ['Reconciliation(s)', reconciliations.length > 0 ? reconciliations.map(r => `${r.status} | Variance: ${r.totalVariance || '0'}`).join(', ') : 'None found'],
      ],
      theme: 'grid',
      styles: { fontSize: 9 },
      headStyles: { fillColor: [75, 63, 255] },
    });

    // Footer on summary pages
    const summaryPageCount = doc.internal.pages.length - 1;
    for (let i = 1; i <= summaryPageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`OpenGov EMC — Ticket 360 Audit Report — Summary Page ${i}`, pageW / 2, 290, { align: 'center' });
    }

    // ── Append HTML-style load ticket page ───────────────────
    {
      doc.addPage();
      const tw = doc.internal.pageSize.getWidth();
      let ty = 15;

      // Header bar
      doc.setFillColor(30, 41, 59);
      doc.rect(0, 0, tw, 22, 'F');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text('DEBRIS LOAD TICKET', tw / 2, 8, { align: 'center' });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(139, 92, 246);
      doc.text(ticket.ticketNumber, tw / 2, 17, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      ty = 30;

      // Subtitle row
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const subtitleParts = [
        `Type: ${DEBRIS_TYPE_LABELS[ticket.debrisType as DebrisType] ?? ticket.debrisType}`,
        meta.ticketType ? `Class: ${meta.ticketType}` : null,
        meta.invoiceNumber ? `Invoice: ${meta.invoiceNumber}` : null,
      ].filter(Boolean).join('   ·   ');
      doc.text(subtitleParts, tw / 2, ty, { align: 'center' });
      ty += 8;

      const loadPct = meta.loadPercentage || ticket.loadingLoadPct || ticket.towerLoadPct || '';
      const truckNum = truck?.truckNumber || meta.truckNumber || '—';
      const truckCap = truck?.capacityCY || meta.truckCapacity || '';

      // Loading + Drop-off side by side (4-column table)
      autoTable(doc, {
        startY: ty,
        head: [['LOADING', '', 'DROP OFF', '']],
        body: [
          ['Date', fmtDate(meta.loadDate), 'Date', fmtDate(meta.disposalDate)],
          ['Time', meta.loadTime || '—', 'Time', meta.disposalTime || '—'],
          ['GPS', meta.pickupLat ? `${parseFloat(meta.pickupLat).toFixed(5)}, ${parseFloat(meta.pickupLng).toFixed(5)}` : '—', 'Site', site?.siteName || meta.disposalSiteName || '—'],
          ['Load %', loadPct ? `${loadPct}%` : '—', 'Load Call', meta.loadPercentage ? `${meta.loadPercentage}%` : '—'],
          ['Monitor', ticket.loadingMonitorName || meta.loadMonitorId || '—', 'Monitor', ticket.towerMonitorName || meta.disposalMonitorName || '—'],
          ['Monitor ID', meta.loadMonitorId || '—', 'Mileage', meta.distanceMiles ? `${meta.distanceMiles} mi` : '—'],
        ],
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [30, 41, 59], fontStyle: 'bold', halign: 'left' },
        columnStyles: {
          0: { cellWidth: 22, fontStyle: 'bold', fillColor: [248, 250, 252], textColor: [100, 116, 139] },
          2: { cellWidth: 22, fontStyle: 'bold', fillColor: [248, 250, 252], textColor: [100, 116, 139] },
        },
      });
      ty = (doc as any).lastAutoTable.finalY + 6;

      // Truck + Billing side by side
      autoTable(doc, {
        startY: ty,
        head: [['TRUCK', '', 'CLIN BILLING', '']],
        body: [
          ['Truck #', truckNum, 'CLIN Code', meta.clinCode || '—'],
          ['Type', truck?.truckType || meta.truckType || '—', 'Description', meta.clinDescription || '—'],
          ['Capacity', truckCap ? `${truckCap} CY` : '—', 'Unit', 'CY'],
          ['Driver', truck?.driverName || '—', 'Cubic Yards', cy > 0 ? `${cy.toFixed(2)} CY` : '—'],
          ['License', truck?.licensePlate || '—', 'Rate', transactionRate > 0 ? `${fmtMoney(transactionRate)}/CY` : '—'],
          ['Contractor', contractor?.companyName || meta.contractorName || '—', 'Ext. Cost', extendedCost > 0 ? fmtMoney(extendedCost) : '—'],
          ['Subcontractor', meta.subcontractorName || '—', '', ''],
        ],
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [30, 41, 59], fontStyle: 'bold', halign: 'left' },
        columnStyles: {
          0: { cellWidth: 24, fontStyle: 'bold', fillColor: [248, 250, 252], textColor: [100, 116, 139] },
          2: { cellWidth: 24, fontStyle: 'bold', fillColor: [248, 250, 252], textColor: [100, 116, 139] },
        },
      });
      ty = (doc as any).lastAutoTable.finalY + 4;

      // Capacity check badge
      if (truckCap && cy > 0) {
        const truckCapNum = parseFloat(truckCap);
        const capOk = cy <= truckCapNum * 1.05;
        doc.setFillColor(...(capOk ? [220, 252, 231] : [254, 226, 226]) as [number, number, number]);
        doc.roundedRect(14, ty, tw - 28, 10, 2, 2, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(...(capOk ? [3, 119, 48] : [185, 28, 28]) as [number, number, number]);
        const capMsg = capOk
          ? `✓ Load OK: ${cy.toFixed(2)} CY / ${truckCap} CY capacity`
          : `⚠ Capacity exceeded: ${cy.toFixed(2)} CY / ${truckCap} CY capacity`;
        doc.text(capMsg, tw / 2, ty + 6.5, { align: 'center' });
        doc.setTextColor(0, 0, 0);
        ty += 14;
      }

      // Footer
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(148, 163, 184);
      doc.text(`Tampa EMC  ·  Ticket ID ${ticket.id}  ·  DR-4834-FL  ·  Generated ${new Date().toLocaleString()}`, tw / 2, 288, { align: 'center' });
      doc.setTextColor(0, 0, 0);
    }

    // ── Merge source documents using pdf-lib ────────────────
    const { PDFDocument } = await import('pdf-lib');
    const mergedPdf = await PDFDocument.create();

    // 1. Add our summary pages
    const summaryBytes = doc.output('arraybuffer');
    const summaryDoc = await PDFDocument.load(summaryBytes);
    const summaryPages = await mergedPdf.copyPages(summaryDoc, summaryDoc.getPageIndices());
    summaryPages.forEach(p => mergedPdf.addPage(p));

    // Helper to fetch and append a PDF document (optionally only a specific page)
    const appendPdf = async (docId: string, separatorTitle: string, separatorSubtitle: string, specificPage?: number) => {
      try {
        const rawUrl = await getV2FileDownloadUrl(docId);
        const proxyUrl = `/api/filepreview?url=${encodeURIComponent(rawUrl)}`;
        const res = await fetch(proxyUrl);
        if (res.ok) {
          const bytes = await res.arrayBuffer();
          try {
            const srcDoc = await PDFDocument.load(bytes);
            const sepPage = mergedPdf.addPage();
            const { height } = sepPage.getSize();
            sepPage.drawText(separatorTitle, { x: 50, y: height - 100, size: 20 });
            sepPage.drawText(separatorSubtitle, { x: 50, y: height - 130, size: 14 });
            if (specificPage && specificPage >= 1 && specificPage <= srcDoc.getPageCount()) {
              // Only copy the specific page (0-based index)
              const [page] = await mergedPdf.copyPages(srcDoc, [specificPage - 1]);
              mergedPdf.addPage(page);
            } else {
              const pages = await mergedPdf.copyPages(srcDoc, srcDoc.getPageIndices());
              pages.forEach(p => mergedPdf.addPage(p));
            }
            return true;
          } catch { /* corrupted PDF */ }
        }
      } catch { /* download failed */ }
      return false;
    };

    // 2. Append load ticket PDF (only the specific page for this ticket)
    if (ticketDocs.haulTicketDocId) {
      const haulPage = (ticketDocs as any).haulTicketPage as number | undefined;
      await appendPdf(ticketDocs.haulTicketDocId,
        'ATTACHMENT: ORIGINAL LOAD TICKET',
        `Ticket #${ticket.ticketNumber}${haulPage ? ` — Page ${haulPage}` : ''}`,
        haulPage);
    }

    // 3. Append truck cert PDF
    // Search: truck TRMETA → related docs → all docs by truck number
    let certDocId = '';
    const truckNotes = truck?.notes || '';
    const certMetaMatch = truckNotes.match(/\|\|\|TRMETA\|\|\|([\s\S]*?)\|\|\|ENDTRMETA\|\|\|/);
    if (certMetaMatch) {
      try { certDocId = JSON.parse(certMetaMatch[1]).documentId || ''; } catch {}
    }
    if (!certDocId) {
      const certDoc = documents.find(d =>
        (d.documentType === 'truck_certification' || d.documentType === 'truck_cert')
      );
      if (certDoc) {
        const m = (certDoc.notes || '').match(/\|\|\|DOCID\|\|\|([^|]+)\|\|\|/);
        certDocId = m?.[1] || '';
      }
    }
    if (certDocId) {
      await appendPdf(certDocId,
        'ATTACHMENT: TRUCK CERTIFICATION',
        `Truck #${truck?.truckNumber || ''}  —  Capacity: ${truck?.capacityCY || '?'} CY`);
    }

    // Save the merged PDF
    const mergedBytes = await mergedPdf.save();
    const blob = new Blob([mergedBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `FEMA-Audit-Ticket-${ticket.ticketNumber || ticket.id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);

    } catch (e) {
      console.error('PDF generation error:', e);
      notifications.show({ message: 'Failed to generate audit package', color: 'var(--color-error)' });
    } finally {
      setExporting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Text size="lg" c="dimmed">Loading ticket data...</Text>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div style={{ padding: 40 }}>
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate('/debris/tickets')} mb="md">
          Back to Load Tickets
        </Button>
        <Paper p="xl" withBorder style={{ textAlign: 'center' }}>
          <IconAlertTriangle size={48} color="var(--color-error)" style={{ marginBottom: 12 }} />
          <Title order={3} c="red">{error || 'Ticket not found'}</Title>
        </Paper>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* ── Header ────────────────────────────────────────── */}
      <Group justify="space-between" mb="md" style={{ flexWrap: 'wrap' }}>
        <Group gap="sm">
          <Button variant="subtle" size="compact-sm" leftSection={<IconArrowLeft size={14} />}
            onClick={() => navigate('/debris/tickets')}>
            Back to Load Tickets
          </Button>
        </Group>
        <Group gap={8}>
          {/* View Original Ticket — renders from DB data as HTML */}
          <Button size="sm" variant="light" leftSection={<IconSearch size={16} />}
            onClick={() => setShowTicketHtml(true)}
            color="#0285C4">
            View Original Ticket
          </Button>
          <Button size="sm" leftSection={<IconDownload size={16} />}
            onClick={generatePDF} color="var(--color-primary-700)" loading={exporting}>
            {exporting ? 'Building Package...' : 'Export FEMA Audit Package'}
          </Button>
        </Group>
      </Group>

      <Group gap="md" mb="lg" align="flex-end">
        <Title order={2} style={{ margin: 0 }}>Ticket #{ticket.ticketNumber || ticket.id}</Title>
        <Badge size="lg" color={STATUS_COLORS[ticket.status] ?? '#939598'}>
          {TICKET_STATUS_LABELS[ticket.status as TicketStatus] ?? ticket.status}
        </Badge>
        {ticket.handLoaded === 'true' && (
          <Badge size="sm" color="#F86A0B">Hand-Loaded (50% cap)</Badge>
        )}
      </Group>

      <Stack gap={16}>
        {/* ── Section 1: Stat Cards ─────────────────────────── */}
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
          <StatCard label="CY Hauled" value={cy > 0 ? cy.toLocaleString() : '-'} accent="#037730" icon={<IconTruck size={20} />} />
          <StatCard label="Extended Cost" value={extendedCost > 0 ? fmtMoney(extendedCost) : '-'} accent="#0285C4" icon={<IconReceipt size={20} />} />
          <StatCard label="CLIN Code" value={clinLabel(clinCode)} accent="#A627FF" icon={<IconFileText size={20} />} />
          <StatCard label="Rate" value={transactionRate > 0 ? `${fmtMoney(transactionRate)}/CY` : '-'} accent="#C68700" icon={<IconReceipt size={20} />} />
        </SimpleGrid>

        {/* ── Section 2: Chain of Custody Timeline ───────────── */}
        <Paper p="lg" withBorder radius="md">
          <Title order={4} mb="md" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconClock size={20} color="var(--color-primary-700)" /> Chain of Custody
          </Title>

          <div style={{ position: 'relative', paddingLeft: 32 }}>
            {/* Connecting line */}
            <div style={{
              position: 'absolute', left: 11, top: 12, bottom: 12, width: 2,
              background: 'linear-gradient(to bottom, #037730, #0285C4, #A627FF)',
            }} />

            {/* PICKUP */}
            <TimelineNode color="#037730" label="PICKUP" icon="location">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px' }}>
                <KV label="Date" value={meta.loadDate ? fmtDate(meta.loadDate) : fmtTime(meta.pickupTime || ticket.meta.pickupTime || '')} />
                <KV label="Time" value={meta.loadTime || '-'} />
                <KV label="Location" value={
                  (meta.pickupLat && meta.pickupLng)
                    ? `${parseFloat(meta.pickupLat).toFixed(5)}, ${parseFloat(meta.pickupLng).toFixed(5)}`
                    : (meta.pickupAddress || '-')
                } />
                <KV label="Monitor" value={`${meta.loadMonitorName || ticket.loadingMonitorName || '-'}${meta.loadMonitorId ? ` (ID: ${meta.loadMonitorId})` : ''}`} />
                <KV label="Load Estimate" value={ticket.loadingLoadPct ? `${ticket.loadingLoadPct}%` : '-'} />
                {meta.roadName && <KV label="Road" value={meta.roadName} />}
                {meta.roadOwnerName && <KV label="Road Owner" value={meta.roadOwnerName} bold />}
                {meta.roadClass && <KV label="Road Class" value={meta.roadClass} />}
              </div>
              {ticket.loadingMonitorSignature && (
                <div style={{ marginTop: 8 }}>
                  <Text size="xs" c="dimmed">Loading Monitor Signature</Text>
                  <img src={ticket.loadingMonitorSignature} alt="Loading monitor sig" style={{ maxWidth: 140, maxHeight: 50, border: '1px solid #eee', borderRadius: 4 }} />
                </div>
              )}
            </TimelineNode>

            {/* IN TRANSIT */}
            <TimelineNode color="#0285C4" label="IN TRANSIT" icon="truck">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px' }}>
                <KV label="Truck" value={truckNumber ? `#${truckNumber}` : (ticket.truckId ? `ID: ${ticket.truckId}` : '-')} />
                <KV label="Capacity" value={truck ? `${truck.capacityCY} CY` : (meta.truckCapacity ? `${meta.truckCapacity} CY` : '-')} />
                <KV label="Contractor" value={contractor?.companyName || meta.contractorName || '-'} />
                <KV label="Subcontractor" value={meta.subcontractorName || '-'} />
                {meta.distanceMiles && <KV label="Distance" value={`${parseFloat(meta.distanceMiles).toFixed(1)} mi`} />}
              </div>
            </TimelineNode>

            {/* DISPOSAL */}
            <TimelineNode color="#A627FF" label="DISPOSAL" icon="building">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px' }}>
                <KV label="Date" value={meta.disposalDate ? fmtDate(meta.disposalDate) : '-'} />
                <KV label="Time" value={meta.disposalTime || '-'} />
                <KV label="Site" value={meta.disposalSiteName || site?.siteName || '-'} />
                <KV label="Monitor" value={`${meta.disposalMonitorName || ticket.towerMonitorName || '-'}${meta.disposalMonitorId ? ` (ID: ${meta.disposalMonitorId})` : ''}`} />
                <KV label="Tower Estimate" value={ticket.towerLoadPct ? `${ticket.towerLoadPct}%` : '-'} />
                <KV label="Verified CY" value={ticket.calculatedCubicYards || '-'} />
              </div>
              {ticket.towerMonitorSignature && (
                <div style={{ marginTop: 8 }}>
                  <Text size="xs" c="dimmed">Tower Monitor Signature</Text>
                  <img src={ticket.towerMonitorSignature} alt="Tower monitor sig" style={{ maxWidth: 140, maxHeight: 50, border: '1px solid #eee', borderRadius: 4 }} />
                </div>
              )}
              {ticket.verifiedAt && (
                <Text size="xs" c="dimmed" mt={4}>Verified at: {fmtTime(ticket.verifiedAt)}</Text>
              )}
            </TimelineNode>
          </div>
        </Paper>

        {/* ── Section 3: Billing & Invoice ──────────────────── */}
        <Paper p="lg" withBorder radius="md">
          <Title order={4} mb="md" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconReceipt size={20} color="#0285C4" /> Billing & Invoice
          </Title>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px 24px' }}>
            <KV label="CLIN Code" value={clinLabel(clinCode)} />
            <KV label="Description" value={clinDescription || clinDesc(clinCode) || (rateSchedule?.description || '-')} />
            <KV label="Rate ($/CY)" value={transactionRate > 0 ? fmtMoney(transactionRate) : (rateSchedule?.rateTier1 ? `$${rateSchedule.rateTier1}` : '-')} />
            <KV label="Quantity (CY)" value={ticket.calculatedCubicYards || '-'} />
            <KV label="Extended Cost" value={extendedCost > 0 ? fmtMoney(extendedCost) : '-'} bold />
            <KV label="Invoice #" value={meta.invoiceNumber || (invoices.length > 0 ? invoices.map(i => `#${i.invoiceNumber}`).join(', ') : '-')} />
          </div>

          {invoices.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Text size="xs" fw={600} c="dimmed" mb={4}>Related Invoices</Text>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ padding: '6px 12px', textAlign: 'left' }}>Invoice #</th>
                      <th style={{ padding: '6px 12px', textAlign: 'left' }}>Period</th>
                      <th style={{ padding: '6px 12px', textAlign: 'right' }}>Amount</th>
                      <th style={{ padding: '6px 12px', textAlign: 'left' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(inv => (
                      <tr key={inv.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '6px 12px' }}>{inv.invoiceNumber || '-'}</td>
                        <td style={{ padding: '6px 12px' }}>{fmtDate(inv.periodStart)} - {fmtDate(inv.periodEnd)}</td>
                        <td style={{ padding: '6px 12px', textAlign: 'right' }}>{inv.totalAmount ? fmtMoney(parseFloat(inv.totalAmount)) : '-'}</td>
                        <td style={{ padding: '6px 12px' }}><Badge size="xs">{inv.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {reconciliations.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Text size="xs" fw={600} c="dimmed" mb={4}>Reconciliation Status</Text>
              {reconciliations.map(rec => (
                <Group key={rec.id} gap="md" style={{ padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <Badge size="sm" color={rec.status === 'approved' ? 'var(--color-success)' : rec.status === 'disputed' ? 'var(--color-error)' : '#C68700'}>
                    {rec.status}
                  </Badge>
                  <Text size="xs">Period: {fmtDate(rec.periodStart)} - {fmtDate(rec.periodEnd)}</Text>
                  <Text size="xs">Invoiced: {rec.totalInvoiced ? fmtMoney(parseFloat(rec.totalInvoiced)) : '-'}</Text>
                  <Text size="xs">Verified: {rec.totalVerified ? fmtMoney(parseFloat(rec.totalVerified)) : '-'}</Text>
                  <Text size="xs" c={parseFloat(rec.totalVariance || '0') !== 0 ? 'red' : undefined}>
                    Variance: {rec.totalVariance ? fmtMoney(parseFloat(rec.totalVariance)) : '$0.00'}
                  </Text>
                </Group>
              ))}
            </div>
          )}
        </Paper>

        {/* ── Section 4: Truck Certification ────────────────── */}
        <Paper p="lg" withBorder radius="md">
          <Title order={4} mb="md" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconTruck size={20} color="#037730" /> Truck Certification
          </Title>

          {truck ? (
            <>
              {(() => {
                const trMeta = decodeTrMeta(truck.notes);
                const certForThisEvent = !ticketIncidentId || !trMeta.certIncidentId || trMeta.certIncidentId === ticketIncidentId;
                if (!certForThisEvent) {
                  return (
                    <div>
                      <KV label="Truck #" value={truck.truckNumber} />
                      <Text size="sm" c="dimmed" mt="sm">
                        This truck was certified under a different event. Upload a new certification via Package Processor for this event.
                      </Text>
                    </div>
                  );
                }
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px 24px' }}>
                    <KV label="Truck #" value={truck.truckNumber} />
                    <KV label="Capacity (CY)" value={truck.capacityCY || '-'} />
                    <KV label="Vehicle Type" value={trMeta.vehicleType || truck.truckType || '-'} />
                    <KV label="License Plate" value={truck.licensePlate || '-'} />
                    <KV label="Tag State" value={trMeta.tagState || '-'} />
                    <KV label="Tag Expiration" value={trMeta.tagExpiration || '-'} />
                    <KV label="Subcontractor" value={trMeta.subcontractor || '-'} />
                    <KV label="Driver" value={truck.driverName || '-'} />
                    <KV label="Driver Phone" value={truck.driverPhone || '-'} />
                    <div>
                      <Text size="xs" c="dimmed">Cert Status</Text>
                      <Badge size="sm" color={truck.certStatus === 'certified' ? 'var(--color-success)' : truck.certStatus === 'expired' ? 'var(--color-error)' : '#C68700'}>
                        {truck.certStatus || 'Unknown'}
                      </Badge>
                    </div>
                    <KV label="Certified Date" value={fmtDate(truck.certifiedDate)} />
                    <KV label="Expires" value={fmtDate(truck.certExpiresAt)} />
                    <KV label="Has Tailgate" value={truck.hasTailgate === 'true' ? 'Yes' : truck.hasTailgate === 'false' ? 'No' : '-'} />
                    {trMeta.tareWeight && <KV label="Tare Weight" value={trMeta.tareWeight} />}
                    {trMeta.certifiedByName && <KV label="Certified By" value={trMeta.certifiedByName} />}
                  </div>
                );
              })()}

              {/* View Truck Cert PDF — check truck TRMETA, related docs, then all documents */}
              {(() => {
                const truckNum = truck?.truckNumber || '';
                // Source 1: truck's TRMETA documentId
                const truckNotes = truck?.notes || '';
                const trMetaMatch = truckNotes.match(/\|\|\|TRMETA\|\|\|([\s\S]*?)\|\|\|ENDTRMETA\|\|\|/);
                let certDocId = '';
                let certFileName = '';
                if (trMetaMatch) {
                  try {
                    const trMetaParsed = JSON.parse(trMetaMatch[1]);
                    certDocId = trMetaParsed.documentId || '';
                    certFileName = trMetaParsed.certFileName || '';
                  } catch {}
                }
                // Source 2: related documents with truck_certification type
                if (!certDocId) {
                  const certDoc = documents.find(d =>
                    (d.documentType === 'truck_certification' || d.documentType === 'truck_cert') &&
                    (truckNum ? (d.title || '').includes(truckNum) || (d.notes || '').includes(truckNum) : true)
                  );
                  if (certDoc) {
                    const docIdMatch = (certDoc.notes || '').match(/\|\|\|DOCID\|\|\|([^|]+)\|\|\|/);
                    certDocId = docIdMatch?.[1] || parseDocId(certDoc.notes) || '';
                    certFileName = certDoc.title || '';
                  }
                }
                // Source 3: search local documents (already filtered to this ticket/truck)
                if (!certDocId && truckNum) {
                  const certDoc = documents.find(d => {
                    const type = d.documentType || '';
                    return (type === 'truck_certification' || type === 'truck_cert');
                  });
                  if (certDoc) {
                    const docIdMatch = (certDoc.notes || '').match(/\|\|\|DOCID\|\|\|([^|]+)\|\|\|/i);
                    certDocId = docIdMatch?.[1] || '';
                    certFileName = certDoc.title || '';
                  }
                }
                if (!certDocId) return null;
                return (
                  <Group mt="md">
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconFileTypePdf size={14} />}
                      loading={pdfLoading === certDocId}
                      onClick={(e: React.MouseEvent) => { e.stopPropagation(); viewPdf(certDocId, `Truck Cert — #${truck?.truckNumber || ''}`); }}
                    >
                      View Truck Cert
                    </Button>
                    {certFileName && (
                      <Text size="xs" c="dimmed">{certFileName}</Text>
                    )}
                  </Group>
                );
              })()}

              {capacityExceeded && (
                <Paper p="sm" mt="md" radius="sm" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
                  <Group gap={8}>
                    <IconAlertTriangle size={18} color="var(--color-error)" />
                    <Text size="sm" fw={600} c="red">
                      Load exceeds certified capacity ({cy.toLocaleString()} CY hauled vs {truckCapacity.toLocaleString()} CY certified)
                    </Text>
                  </Group>
                </Paper>
              )}
            </>
          ) : (
            <Paper p="sm" radius="sm" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
              <Group gap={8}>
                <IconAlertTriangle size={18} color="#C68700" />
                <Text size="sm" fw={500} c="#92400e">
                  No truck certification on file for truck #{truckNumber || ticket.truckId || 'unknown'}
                </Text>
              </Group>
            </Paper>
          )}
        </Paper>

        {/* ── Section 5: Disposal Site ──────────────────────── */}
        <Paper p="lg" withBorder radius="md">
          <Title order={4} mb="md" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconBuilding size={20} color="#A627FF" /> Disposal Site
          </Title>

          {site ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px 24px' }}>
                <KV label="Site Name" value={site.siteName} />
                <KV label="Type" value={site.siteType || '-'} />
                <KV label="Address" value={site.address || '-'} />
                <KV label="Accepted Types" value={siteAcceptedTypes.join(', ') || '-'} />
                <KV label="Capacity (CY)" value={site.capacityCubicYards || '-'} />
                <KV label="Current Volume" value={site.currentVolume || '-'} />
                <div>
                  <Text size="xs" c="dimmed">Status</Text>
                  <Badge size="sm" color={site.status === 'open' ? 'var(--color-success)' : site.status === 'full' ? '#C68700' : '#939598'}>
                    {site.status || '-'}
                  </Badge>
                </div>
              </div>

              {/* DEP Authorization from notes meta */}
              {(() => {
                const siteNotesMeta = decodeMeta(site.notes);
                if (siteNotesMeta.depAuthorizationId) {
                  return <KV label="DEP Authorization ID" value={siteNotesMeta.depAuthorizationId} />;
                }
                return null;
              })()}

              {wasteTypeViolation && (
                <Paper p="sm" mt="md" radius="sm" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
                  <Group gap={8}>
                    <IconAlertTriangle size={18} color="var(--color-error)" />
                    <Text size="sm" fw={600} c="red">
                      Debris type [{debrisTypeLabel}] not authorized for this site (authorized: {siteAcceptedTypes.join(', ')})
                    </Text>
                  </Group>
                </Paper>
              )}
            </>
          ) : meta.disposalSiteName ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px 24px' }}>
              <KV label="Site Name" value={meta.disposalSiteName} />
              {meta.disposalDate && <KV label="Disposal Date" value={fmtDate(meta.disposalDate)} />}
              {meta.disposalTime && <KV label="Disposal Time" value={meta.disposalTime} />}
              {meta.distanceMiles && <KV label="Distance" value={`${parseFloat(meta.distanceMiles).toFixed(1)} mi`} />}
            </div>
          ) : (
            <Text size="sm" c="dimmed">No disposal site record found for this ticket.</Text>
          )}
        </Paper>

        {/* ── Section 6: Related Documents ──────────────────── */}
        {(() => {
          const ticketNum = ticket?.ticketNumber?.toLowerCase() ?? '';
          // Only show docs explicitly referencing this ticket number — exclude internal
          // scan types (ticket_image, ticket_page_index) and truck certs (matched by
          // truck, not ticket; still retained in state for PDF generation)
          const relatedDocs = documents.filter(d => {
            if (d.documentType === 'ticket_image' || d.documentType === 'ticket_page_index' || d.documentType === 'truck_cert') return false;
            if (!ticketNum) return false;
            return d.title.toLowerCase().includes(ticketNum) || d.notes.toLowerCase().includes(ticketNum);
          });
          if (relatedDocs.length === 0) return null;
          return (
            <Paper p="lg" withBorder radius="md">
              <Title order={4} mb="md" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <IconFileText size={20} color="#0285C4" /> Related Documents
              </Title>
              <Stack gap={6}>
                {relatedDocs.map(doc => {
                  const docId = parseDocId(doc.notes);
                  return (
                    <Group key={doc.id} justify="space-between" p="xs" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                      <Text size="sm" fw={500}>{doc.title}</Text>
                      {docId ? (
                        <Button size="xs" variant="light" leftSection={<IconFileTypePdf size={14} />}
                          loading={pdfLoading === docId}
                          onClick={() => viewPdf(docId, doc.title)}>
                          View
                        </Button>
                      ) : <Text size="xs" c="dimmed">No file</Text>}
                    </Group>
                  );
                })}
              </Stack>
            </Paper>
          );
        })()}

        {/* ── Section 6b: FEMA Supporting Document Slots ──── */}
        <Paper p="lg" withBorder radius="md">
          <Title order={4} mb="md" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconPaperclip size={20} color="#0E7490" /> FEMA Supporting Documents
          </Title>
          <Text size="xs" c="dimmed" mb="md">
            Attach the original source document for this ticket. Load tickets contain monitor verification and tonnage data.
          </Text>
          <Stack gap={8}>
            {([
              { slot: 'haulTicketDocId' as const, label: 'Load Ticket', hint: 'ADMS load ticket containing monitor verification, tonnage, and GPS data' },
            ]).map(({ slot, label, hint }) => {
              const docId = ticketDocs[slot];
              return (
                <Group key={slot} justify="space-between" p="sm" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <div>
                    <Text size="sm" fw={500}>{label}</Text>
                    <Text size="xs" c="dimmed">{hint}</Text>
                  </div>
                  <Group gap={6}>
                    {docId ? (
                      <>
                        <Badge size="sm" style={{ background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}>Attached</Badge>
                        <Button size="xs" variant="subtle" leftSection={<IconEye size={13} />}
                          loading={pdfLoading === docId}
                          onClick={() => viewPdf(docId, label, undefined, (ticketDocs as any).haulTicketPage)}>
                          View{(ticketDocs as any).haulTicketPage ? ` (p.${(ticketDocs as any).haulTicketPage})` : ''}
                        </Button>
                      </>
                    ) : (
                      <Badge size="sm" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>Missing</Badge>
                    )}
                    <label style={{ cursor: 'pointer' }}>
                      <input type="file" accept=".pdf,image/*" style={{ display: 'none' }}
                        onChange={e => { const f = e.target.files?.[0]; if (f) saveTicketDoc(slot, f); e.target.value = ''; }} />
                      <Button size="xs" variant="light" component="span" loading={docSaving === slot}
                        leftSection={<IconPaperclip size={13} />}>
                        {docId ? 'Replace' : 'Attach'}
                      </Button>
                    </label>
                  </Group>
                </Group>
              );
            })}
          </Stack>
        </Paper>

        {/* ── Section 7: FEMA Audit Package Button ──────────── */}
        <Paper p="lg" withBorder radius="md" style={{ background: '#f8fafc' }}>
          <Group justify="space-between" align="center">
            <div>
              <Title order={4} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <IconShield size={20} color="var(--color-primary-700)" /> FEMA Audit Package
              </Title>
              <Text size="sm" c="dimmed">
                Generate a complete single-ticket audit package with all chain of custody, billing, and certification details.
              </Text>
            </div>
            <Button size="md" leftSection={<IconDownload size={18} />}
              onClick={generatePDF} color="var(--color-primary-700)">
              Generate Single-Ticket Audit Package
            </Button>
          </Group>
        </Paper>

        {/* ── Notes ─────────────────────────────────────────── */}
        {ticket.cleanNotes && (
          <Paper p="lg" withBorder radius="md">
            <Title order={4} mb="sm">Notes</Title>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{ticket.cleanNotes}</Text>
          </Paper>
        )}
      </Stack>

      {/* ── Inline PDF Viewer Modal ──────────────────────────── */}
      <Modal opened={!!pdfViewerUrl} onClose={() => { setPdfViewerUrl(null); setPdfViewerTitle(''); setPdfSearchText(''); setPdfViewerPage(undefined); }}
        title={pdfViewerTitle} fullScreen
        styles={{ body: { padding: 0 }, header: { padding: '12px 20px' } }}>
        {pdfViewerUrl && (
          <iframe src={pdfViewerUrl} title={pdfViewerTitle}
            style={{ width: '100%', height: 'calc(100vh - 60px)', border: 'none' }} />
        )}
      </Modal>

      {/* ── HTML Ticket View Modal ──────────────────────────── */}
      <Modal opened={showTicketHtml} onClose={() => setShowTicketHtml(false)}
        title={`Load Ticket — #${ticket?.ticketNumber}`} fullScreen
        styles={{ body: { padding: 0, height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column' }, header: { padding: '12px 20px' } }}>
        {showTicketHtml && ticket && (
          <TicketHtmlView ticket={ticket as any} truck={truck as any} site={site as any} contractor={contractor as any} />
        )}
      </Modal>
    </div>
  );
}

// ── Subcomponents ──────────────────────────────────────────────

function StatCard({ label, value, accent, icon }: { label: string; value: string; accent: string; icon: React.ReactNode }) {
  return (
    <Paper p="md" radius="md" withBorder
      style={{
        borderRadius: 12, border: '1px solid #e8eaf0',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        transition: 'transform 0.15s, box-shadow 0.15s',
        cursor: 'default',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
        (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
        (e.currentTarget as HTMLElement).style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)';
      }}
    >
      <div style={{ color: accent, marginBottom: 4 }}>{icon}</div>
      <Text style={{ fontSize: 28, fontWeight: 800, color: accent, lineHeight: 1.1 }}>{value}</Text>
      <Text size="xs" c="dimmed" mt={4}>{label}</Text>
    </Paper>
  );
}

function TimelineNode({ color, label, icon, children }: { color: string; label: string; icon: string; children: React.ReactNode }) {
  const iconMap: Record<string, React.ReactNode> = {
    location: <IconMapPin size={14} />,
    truck: <IconTruck size={14} />,
    building: <IconBuilding size={14} />,
  };
  return (
    <div style={{ position: 'relative', marginBottom: 24, paddingLeft: 12 }}>
      {/* Dot */}
      <div style={{
        position: 'absolute', left: -28, top: 4, width: 18, height: 18,
        borderRadius: '50%', background: color, display: 'flex',
        alignItems: 'center', justifyContent: 'center', color: '#fff',
        boxShadow: `0 0 0 4px ${color}20`,
      }}>
        {iconMap[icon] || null}
      </div>
      <Text size="xs" fw={700} style={{ color, textTransform: 'uppercase', letterSpacing: '0.05em' }} mb={4}>
        {label}
      </Text>
      {children}
    </div>
  );
}

function KV({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm" fw={bold ? 700 : 500}>{value}</Text>
    </div>
  );
}
